import { ReactNode, useState } from 'react';
import { useMutation } from 'react-query';
import { api } from '../../../services/api';
import { Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import MoreMenu from '../../ui/MoreMenu';
import { confirmAction } from '../../ui/Confirm';
import { ErrorNotice, SkeletonRows } from '../../ui/States';
import { errorMessage } from '../../../lib/programs';
import { openBlankTab, showPrintable } from '../../../lib/printable';
import {
  CommonTexts,
  InfoData,
  InfoPackage,
  PackageSection,
  SECTION_LABEL,
  useEventInfo,
  useInfoRefresh,
} from '../../../lib/eventInfo';
import type { SimplePanelProps } from '../adapt';

const SECTIONS = Object.keys(SECTION_LABEL) as PackageSection[];
const COMMON: [keyof CommonTexts, string][] = [
  ['parkingEn', 'Parking in English'],
  ['parkingFr', 'Parking in French'],
  ['arrivalEn', 'Arriving in English'],
  ['arrivalFr', 'Arriving in French'],
  ['accessibilityEn', 'Accessibility in English'],
  ['accessibilityFr', 'Accessibility in French'],
];
const day = (at: string) =>
  new Date(at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto', month: 'short', day: 'numeric' });

// Dialog fields (docs/redesign/02-components.md, Forms).
const LABEL = 'block text-[15px] leading-5 font-semibold text-ink';
const INPUT =
  'mt-[6px] block w-full min-h-[44px] px-3 py-[10px] border border-line-strong rounded-sm bg-surface text-[15px] leading-[22px] text-ink font-normal';
const CHECK = 'inline-flex items-center gap-2 min-h-[32px] text-[15px] leading-[22px] text-ink';
const BOX = 'w-[18px] h-[18px] accent-accent';

// Rows stack below 768px: header hidden, each cell on its own line after its column name.
const TABLE = 'w-full border-collapse tabular max-md:block';
const HEAD = 'text-left text-small text-ink-2 border-b border-line-strong max-md:hidden';
const TH = 'py-2 pr-3 last:pr-0 align-bottom font-semibold';
const ROW = 'border-b border-line hover:bg-selected max-md:block max-md:py-3';
const TD = 'py-[11px] pr-3 last:pr-0 align-top text-[15px] leading-[22px] text-ink max-md:block max-md:p-0';
const LABELLED =
  "max-md:before:content-[attr(data-label)_'_·_'] max-md:before:text-ink-3 max-md:before:text-small max-md:before:font-normal";

/** "no parking text (Hotel tab)" → "Parking text (Hotel tab)"; `short` also drops the hint in brackets. */
export const gapText = (g: string, short = false) => {
  const t = g.replace(/^no /, '').replace(/ for it yet\b/, '');
  const s = short ? t.replace(/\s*\(.*\)$/, '') : t;
  return s;
};
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const PARKING_GAP = 'no parking text';

/** True when every package lacks the shared parking text, so the Documents tab offers to add it first. */
export const parkingMissingEverywhere = (info: InfoData | undefined) =>
  Boolean(info?.packages.length && info.packages.every((p) => p.gaps.includes(PARKING_GAP)));

function Field({ label, children, wide = false }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <label className={`${LABEL} ${wide ? 'md:col-span-2' : ''}`}>
      {label}
      {children}
    </label>
  );
}

/** The six shared texts every package prints (parking, arriving, accessibility; English and French). */
export function SharedTextsDialog({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const q = useEventInfo(eventId);
  const refresh = useInfoRefresh(eventId);
  const [texts, setTexts] = useState<CommonTexts | null>(null);
  const value = texts ?? q.data?.common ?? {};
  const save = useMutation(async () => api.put(`/events/${eventId}/info`, { common: value }), {
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  return (
    <Modal
      title="Parking, arrival and accessibility texts"
      why="Every information package prints these, in its language. Save them once here."
      onClose={onClose}
      width={760}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading || !q.data} onClick={() => save.mutate()}>
            Save the texts
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      {q.isLoading ? (
        <SkeletonRows rows={3} />
      ) : (
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 max-md:grid-cols-1">
          {COMMON.map(([k, label]) => (
            <Field key={k} label={label}>
              <textarea
                className={INPUT}
                rows={4}
                value={value[k] ?? ''}
                onChange={(e) => setTexts({ ...value, [k]: e.target.value })}
              />
            </Field>
          ))}
        </div>
      )}
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save the texts.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function PackageEditor({
  pkg,
  info,
  onSave,
  onCancel,
  busy,
  error,
  isNew = false,
}: {
  isNew?: boolean;
  pkg: InfoPackage;
  info: InfoData;
  onSave: (p: InfoPackage) => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  const [p, setP] = useState<InfoPackage>(pkg);
  const set = <K extends keyof InfoPackage>(k: K, v: InfoPackage[K]) => setP({ ...p, [k]: v });
  return (
    <Modal
      title={isNew ? 'Add a package' : `Edit the package: ${pkg.name}`}
      why={
        isNew
          ? 'A new printable page for one audience, in English and French. Nothing is saved until you press Add the package.'
          : "What this audience's page holds. The agenda shows the sessions of the ticked audiences."
      }
      onClose={onCancel}
      width={760}
      footer={
        <>
          <Button variant="primary" disabled={busy || !p.name.trim()} onClick={() => onSave(p)}>
            {isNew ? 'Add the package' : 'Save'}
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
          {!p.name.trim() ? <span className="text-small text-ink-3">Give the package a name to save it.</span> : null}
        </>
      }
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 max-md:grid-cols-1">
        <Field label="Name">
          <input className={INPUT} value={p.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="French name">
          <input className={INPUT} value={p.nameFr ?? ''} onChange={(e) => set('nameFr', e.target.value)} />
        </Field>
        <Field label="For">
          <select className={INPUT} value={p.mode} onChange={(e) => set('mode', e.target.value as InfoPackage['mode'])}>
            <option value="in_person">People coming in person</option>
            <option value="virtual">People joining online</option>
          </select>
        </Field>
        <div className="md:col-span-2">
          <span className={LABEL}>Audiences</span>
          <p className="text-small text-ink-3 mt-[2px]">None ticked: every session is on its agenda.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1">
            {info.audiences.map((a) => (
              <label key={a.key} className={CHECK}>
                <input
                  type="checkbox"
                  className={BOX}
                  checked={p.audienceKeys.includes(a.key)}
                  onChange={(e) =>
                    set(
                      'audienceKeys',
                      e.target.checked ? [...p.audienceKeys, a.key] : p.audienceKeys.filter((k) => k !== a.key),
                    )
                  }
                />
                {a.name}
              </label>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <span className={LABEL}>Sections</span>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1">
            {SECTIONS.map((s) => (
              <label key={s} className={CHECK}>
                <input
                  type="checkbox"
                  className={BOX}
                  checked={Boolean(p.sections[s])}
                  onChange={(e) => set('sections', { ...p.sections, [s]: e.target.checked })}
                />
                {SECTION_LABEL[s]}
              </label>
            ))}
          </div>
        </div>
        <Field label="Notes in English">
          <textarea
            className={INPUT}
            rows={3}
            value={p.notesEn ?? ''}
            onChange={(e) => set('notesEn', e.target.value)}
          />
        </Field>
        <Field label="Notes in French">
          <textarea
            className={INPUT}
            rows={3}
            value={p.notesFr ?? ''}
            onChange={(e) => set('notesFr', e.target.value)}
          />
        </Field>
      </div>
      {error ? (
        <div className="mt-4">
          <ErrorNotice>{error}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function PublishDialog({
  pkg,
  busy,
  error,
  onSave,
  onClose,
}: {
  pkg: InfoPackage;
  busy: boolean;
  error: string | null;
  onSave: (url: string) => void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState(pkg.publishedUrl ?? '');
  return (
    <Modal
      title={`Mark "${pkg.name}" published`}
      why="Records the day it went on the website, with its link, so the table shows where people find it."
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" disabled={busy} onClick={() => onSave(url)}>
            Mark published
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <Field label="Link on the website">
        <input
          className={INPUT}
          placeholder="https://divertmentalhealth.ca/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <span className="block mt-1 text-small font-normal text-ink-3">Optional.</span>
      </Field>
      {error ? (
        <div className="mt-4">
          <ErrorNotice>{error}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

/** Information packages per audience (Before → Documents): printable EN/FR pages from the event's details, prepared
 *  only. The shared texts are a link under the table, unless the tab's main button already offers them. */
export default function InfoPackagesPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useEventInfo(eventId);
  const refresh = useInfoRefresh(eventId);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState<InfoPackage | null>(null);
  const [publishing, setPublishing] = useState<string | null>(null);
  const [texts, setTexts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation(async (body: Record<string, unknown>) => api.put(`/events/${eventId}/info`, body), {
    onSuccess: () => {
      setEditing(null);
      setAdding(null);
      refresh();
    },
    onError: (err) => setError(errorMessage(err, 'Could not save the packages.')),
  });
  const publish = useMutation(
    async ({ key, url, undo }: { key: string; url?: string; undo?: boolean }) =>
      api.post(`/events/${eventId}/info/packages/${key}/published`, { url, undo }),
    {
      onSuccess: () => {
        setPublishing(null);
        refresh();
      },
      onError: (err) => setError(errorMessage(err, 'Could not record it.')),
    },
  );

  if (q.isLoading) return <SkeletonRows rows={4} />;
  if (q.error || !q.data)
    return <ErrorNotice>{errorMessage(q.error, 'Could not load the information packages.')}</ErrorNotice>;
  const info = q.data;
  const strip = (p: InfoPackage) => ({
    key: p.key,
    name: p.name,
    nameFr: p.nameFr,
    mode: p.mode,
    audienceKeys: p.audienceKeys,
    sections: p.sections,
    notesEn: p.notesEn,
    notesFr: p.notesFr,
    publishedUrl: p.publishedUrl,
  });
  const savePackages = (list: InfoPackage[]) => {
    setError(null);
    save.mutate({ packages: list.map(strip) });
  };

  const open = async (key: string, lang: 'en' | 'fr') => {
    setError(null);
    const tab = openBlankTab();
    try {
      const res = await api.get(`/events/${eventId}/info/packages/${key}?lang=${lang}`);
      showPrintable(tab, res.data.data.html);
      refresh();
    } catch (err) {
      tab?.close();
      setError(errorMessage(err, 'Could not prepare the package.'));
    }
  };

  const published = info.packages.filter((p) => p.publishedAt).length;
  const editingPkg = info.packages.find((p) => p.key === editing);
  const publishingPkg = info.packages.find((p) => p.key === publishing);
  return (
    <Panel className="mt-14 max-md:mt-10">
      <PanelHeader title="Information packages" note={`${published} of ${info.packages.length} published`}>
        {canEdit ? (
          <Button
            disabled={save.isLoading}
            onClick={() => {
              setError(null);
              setAdding({
                key: '',
                name: '',
                nameFr: null,
                mode: 'in_person',
                audienceKeys: [],
                sections: { hotel: false, travel: false, parking: true, zoom: false, subEvents: false },
                notesEn: null,
                notesFr: null,
                publishedUrl: null,
                publishedAt: null,
                preparedAt: null,
                audiences: [],
                sessions: 0,
                gaps: [],
              });
            }}
          >
            Add a package
          </Button>
        ) : null}
      </PanelHeader>
      <PanelBody>
        <p className="text-[15px] leading-[22px] text-ink-2 mb-3 max-w-[72ch]">
          One page per audience, in English and French, from the event’s details: days, agenda for that audience, hotel,
          travel rules, parking, Zoom links and contacts. Prepared here, never sent: print it to PDF, publish it on the
          website, then mark it published.
        </p>
        {error ? (
          <div className="mb-3">
            <ErrorNotice>{error}</ErrorNotice>
          </div>
        ) : null}
        {info.packages.length ? (
          <table className={TABLE}>
            <thead className={HEAD}>
              <tr>
                <th className={TH}>Package</th>
                <th className={TH}>For</th>
                <th className={`${TH} text-right`}>Sessions</th>
                <th className={TH}>Still missing</th>
                <th className={TH}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="max-md:block">
              {info.packages.map((p) => (
                <tr key={p.key} className={ROW}>
                  <td className={TD}>
                    <span className="font-semibold">{p.name}</span>
                    <span className="block text-small text-ink-3">
                      {p.publishedAt ? 'Published' : p.preparedAt ? 'Prepared' : 'Not prepared'}
                    </span>
                  </td>
                  <td data-label="For" className={`${TD} ${LABELLED}`}>
                    {p.mode === 'virtual' ? 'Online' : 'In person'} ·{' '}
                    {p.audiences.length ? p.audiences.join(', ') : 'every audience'}
                  </td>
                  <td data-label="Sessions" className={`${TD} ${LABELLED} md:text-right`}>
                    {p.sessions}
                  </td>
                  <td data-label="Missing" className={`${TD} ${LABELLED} text-ink-2`}>
                    {p.gaps.length ? capital(p.gaps.map((g) => gapText(g)).join(', ')) : null}
                    {p.publishedUrl ? (
                      <span className={p.gaps.length ? 'block mt-1' : ''}>
                        On the website:{' '}
                        <a className="break-all" href={p.publishedUrl} target="_blank" rel="noreferrer">
                          {p.publishedUrl}
                        </a>
                        {p.publishedAt ? ` (${day(p.publishedAt)})` : ''}
                      </span>
                    ) : null}
                    {!p.gaps.length && !p.publishedUrl ? <span className="text-ink-3">–</span> : null}
                  </td>
                  <td className={`${TD} md:text-right whitespace-nowrap max-md:pt-2`}>
                    <span className="inline-flex gap-2 items-center">
                      <Button size="sm" onClick={() => open(p.key, 'en')}>
                        English
                      </Button>
                      <Button size="sm" onClick={() => open(p.key, 'fr')}>
                        Français
                      </Button>
                      {canEdit ? (
                        <MoreMenu
                          label={`More for ${p.name}`}
                          items={[
                            { label: 'Edit', onClick: () => setEditing(p.key) },
                            {
                              label: 'Remove',
                              destructive: true,
                              disabled: save.isLoading,
                              onClick: async () => {
                                const ok = await confirmAction(
                                  p.publishedAt
                                    ? `Remove the package “${p.name}”? It is marked published: the page on the website stays until you take it down there.`
                                    : `Remove the package “${p.name}”? Its settings and notes go; the event's details stay.`,
                                  'Remove the package',
                                );
                                if (ok) savePackages(info.packages.filter((x) => x.key !== p.key));
                              },
                            },
                            p.publishedAt
                              ? {
                                  label: 'Undo published',
                                  disabled: publish.isLoading,
                                  onClick: () => {
                                    setError(null);
                                    publish.mutate({ key: p.key, undo: true });
                                  },
                                }
                              : {
                                  label: 'Mark published',
                                  onClick: () => {
                                    setError(null);
                                    setPublishing(p.key);
                                  },
                                },
                          ]}
                        />
                      ) : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-[15px] leading-[22px] text-ink-2">No packages yet.</p>
        )}
        {canEdit && !parkingMissingEverywhere(info) ? (
          <p className="mt-3">
            <button
              type="button"
              className="text-[15px] leading-[22px] text-accent underline underline-offset-[3px] hover:text-ink"
              onClick={() => setTexts(true)}
            >
              Parking, arrival and accessibility texts
            </button>
          </p>
        ) : null}
        <p className="text-small text-ink-3 mt-3">
          {canEdit ? '“More” holds Edit, Mark published and Remove. ' : ''}Key contacts come from the event settings.
        </p>
      </PanelBody>
      {editingPkg ? (
        <PackageEditor
          pkg={editingPkg}
          info={info}
          busy={save.isLoading}
          error={error}
          onCancel={() => setEditing(null)}
          onSave={(np) => savePackages(info.packages.map((x) => (x.key === np.key ? np : x)))}
        />
      ) : null}
      {adding ? (
        <PackageEditor
          isNew
          pkg={adding}
          info={info}
          busy={save.isLoading}
          error={error}
          onCancel={() => setAdding(null)}
          onSave={(np) => savePackages([...info.packages, np])}
        />
      ) : null}
      {publishingPkg ? (
        <PublishDialog
          pkg={publishingPkg}
          busy={publish.isLoading}
          error={error}
          onClose={() => setPublishing(null)}
          onSave={(url) => publish.mutate({ key: publishingPkg.key, url: url || undefined })}
        />
      ) : null}
      {texts ? <SharedTextsDialog eventId={eventId} onClose={() => setTexts(false)} /> : null}
    </Panel>
  );
}
